import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button, Input, Badge, Dropdown, Avatar, Drawer } from "antd";
import {
  ShoppingCartOutlined,
  SearchOutlined,
  AppstoreOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  RightOutlined,
  LeftOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import Logo from "../../../images/logo.png";
import MarketPlaceService from "../../../utils/api/marketplace";
import { notificacionAction } from "../../../actions/notificationActions";
import moment from "moment";
import DollarRateCard from "../../../pages/Dashboard/DollarRateCard";
import "./TopBarStyles.css";
import useAppStore from "../../../store/useAppStore";

const { Search } = Input;

const TopBar = ({
  isCategory,
  categorie,
  onSearch,
  title,
  onLogout,
  menuButton,
}) => {
  const { category, catalog } = useParams();
  const history = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [localUnreadCount, setLocalUnreadCount] = useState(0);
  const [localCartCount, setLocalCartCount] = useState(0);
  const [localNotifications, setLocalNotifications] = useState([]);
  const storeUnreadCount = useAppStore((state) => state.unreadCount);
  const storeCart = useAppStore((state) => state.cart);
  const storeNotifications = useAppStore((state) => state.notifications);
  const storeCategories = useAppStore((state) => state.categories);
  const markNotificationAsRead = useAppStore(
    (state) => state.markNotificationAsRead
  );
  const markAllAsReadStore = useAppStore(
    (state) => state.markAllNotificationsAsRead
  );

  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  });

  useEffect(() => {
    setLocalUnreadCount(storeUnreadCount);
  }, [storeUnreadCount]);

  const getProductsCount = useCallback((products) => {
    if (!Array.isArray(products)) return 0;

    const uniqueProductIds = new Set();

    products.forEach((item) => {
      const itemId = item?.id ?? item?.product_id;
      if (itemId !== undefined && itemId !== null) {
        uniqueProductIds.add(String(itemId));
      }
    });

    return uniqueProductIds.size;
  }, []);

  const syncCartCount = useCallback(() => {
    const storeCount = getProductsCount(storeCart?.products);
    const currentRole = String(user?.role ?? localStorage.getItem("role") ?? "");
    const cartKey = user?.id ? `cart_${user.id}_${currentRole}` : "cart_guest";

    try {
      const localCartRaw = localStorage.getItem(cartKey);
      if (!localCartRaw) {
        setLocalCartCount(storeCount);
        return;
      }

      const localProducts = JSON.parse(localCartRaw);
      const localCount = getProductsCount(localProducts);
      setLocalCartCount(localCount);
    } catch (error) {
      setLocalCartCount(storeCount);
    }
  }, [getProductsCount, storeCart?.products, user?.id, user?.role]);

  useEffect(() => {
    syncCartCount();
  }, [syncCartCount]);

  useEffect(() => {
    setLocalNotifications(storeNotifications);
  }, [storeNotifications]);

  useEffect(() => {
    const handleStorageChange = () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user"));
        setUser(userData);
      } catch {
        setUser(null);
      }
    };

    const handleUserUpdated = () => {
      handleStorageChange();
    };

    const handleCartUpdated = () => {
      syncCartCount();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("userUpdated", handleUserUpdated);
    window.addEventListener("cartUpdated", handleCartUpdated);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("userUpdated", handleUserUpdated);
      window.removeEventListener("cartUpdated", handleCartUpdated);
    };
  }, [syncCartCount]);

  const role = useMemo(() => {
    return localStorage.getItem("role");
  }, []);

  const searchContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const isExplicitClearRef = useRef(false);

  const roles = useMemo(
    () => ({
      isAdmin: role === "1",
      isFinanzas: role === "2",
      isCompras: role === "3",
      isClient: role === "4",
      isVentas: role === "5",
      isProvider: role === "6",
      isRepartidor: role === "9",
    }),
    [role]
  );

  const canSeeDollarRate = roles.isCompras || roles.isVentas;
  const hasMarketplaceAccess =
    roles.isAdmin || roles.isClient || roles.isCompras || roles.isVentas;
  const isGuest = !user || !role;
  const canBrowseMarketplace = hasMarketplaceAccess || isGuest;
  const shouldShowTopBar = true;
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [lastCommittedSearch, setLastCommittedSearch] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isTabletView, setIsTabletView] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(true);
  const [showAllSubcategories, setShowAllSubcategories] = useState(false);

  const isMountedRef = useRef(true);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    let searchFromLocation = (query.get("search") || "").trim();

    if (!searchFromLocation && location.hash) {
      const hashQueryIndex = location.hash.indexOf("?");
      if (hashQueryIndex !== -1) {
        const hashQuery = location.hash.slice(hashQueryIndex + 1);
        searchFromLocation = (
          new URLSearchParams(hashQuery).get("search") || ""
        ).trim();
      }
    }

    setSearchValue((prev) =>
      prev === searchFromLocation ? prev : searchFromLocation
    );
    setLastCommittedSearch((prev) =>
      prev === searchFromLocation ? prev : searchFromLocation
    );
  }, [location.search, location.hash]);

  const pathChecks = useMemo(() => {
    const path = location.pathname;
    return {
      isCartPage: path === "/marketplace/shopping-cart",
      isCheckoutPage: path === "/marketplace/checkout",
      isMarketplaceRoute: path.startsWith("/marketplace") || path === "/",
      isUserAnalyticsRoute: path.includes("/analytics"),
      isUserOrdersRoute:
        path.includes("/orders") && !path.includes("/orders/details"),
      isSupplierProductsRoute: path.includes("/all-products"),
    };
  }, [location.pathname]);

  const excludedFromSearchRoutes = useMemo(
    () => [
      "/marketplace/suppliers",
      "/marketplace/suppliers/",
      "/marketplace/admin/categories",
      "/marketplace/orders",
      "/marketplace/analytics",
      "/marketplace/all-products",
    ],
    []
  );

  const isExcludedRoute = useMemo(
    () =>
      excludedFromSearchRoutes.some((route) =>
        location.pathname.startsWith(route)
      ) ||
      pathChecks.isUserAnalyticsRoute ||
      pathChecks.isUserOrdersRoute ||
      pathChecks.isSupplierProductsRoute,
    [location.pathname, pathChecks, excludedFromSearchRoutes]
  );

  const showMarketplaceFeatures = useMemo(
    () =>
      pathChecks.isMarketplaceRoute &&
      !pathChecks.isCartPage &&
      !pathChecks.isCheckoutPage &&
      !isExcludedRoute &&
      canBrowseMarketplace,
    [pathChecks, isExcludedRoute, canBrowseMarketplace]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let resizeTimeout;

    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);

      resizeTimeout = setTimeout(() => {
        if (!isMountedRef.current) return;

        const width = window.innerWidth;
        setIsMobileView(width <= 768);
        setIsTabletView(width > 768 && width <= 1024);

        if (width > 768 && isSearchExpanded) {
          setIsSearchExpanded(false);
        }
        if (width > 1024 && isMobileSearchOpen) {
          setIsMobileSearchOpen(false);
        }
        if (width > 1024 && mobileMenuOpen) {
          setMobileMenuOpen(false);
          setActiveCategory(null);
          setActiveSubcategory(null);
          setNavigationHistory([]);
          setShowAllCategories(true);
          setShowAllSubcategories(false);
        }
      }, 150);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, [isSearchExpanded, isMobileSearchOpen, mobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSearchExpanded &&
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setIsSearchExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSearchExpanded]);

  const loadCategories = useCallback(async () => {
    if (!canBrowseMarketplace || categoriesLoaded) return;

    try {
      setLoadingCategories(true);
      const response = await MarketPlaceService.getCategories();

      let categoriesData = [];
      if (
        response.data &&
        response.data.success &&
        Array.isArray(response.data.categories)
      ) {
        categoriesData = response.data.categories;
      } else if (response.data && Array.isArray(response.data)) {
        categoriesData = response.data;
      } else if (response.success && Array.isArray(response.categories)) {
        categoriesData = response.categories;
      } else if (Array.isArray(response)) {
        categoriesData = response;
      }

      if (categoriesData.length > 0 && isMountedRef.current) {
        const transformedCategories = transformCategories(categoriesData);
        setCategories(transformedCategories);
        setCategoriesLoaded(true);
      }
    } catch (error) {
      console.error("Error loading categories:", error);
      if (isMountedRef.current) {
        setCategories([]);
      }
    } finally {
      if (isMountedRef.current) setLoadingCategories(false);
    }
  }, [canBrowseMarketplace, categoriesLoaded]);

  useEffect(() => {
    if (showMarketplaceFeatures && shouldShowTopBar && !categoriesLoaded) {
      loadCategories();
    }
  }, [showMarketplaceFeatures, categoriesLoaded, loadCategories]);

  const markAllAsRead = useCallback(async () => {
    if (localUnreadCount === 0) return;
    markAllAsReadStore();
    dispatch(notificacionAction(false));
  }, [localUnreadCount, markAllAsReadStore, dispatch]);

  const transformCategories = (categoriesData) => {
    const validCategories = categoriesData.filter(
      (cat) => cat && cat.name && cat.slug
    );
    const categoriesMap = {};

    validCategories.forEach((category) => {
      categoriesMap[category.slug] = { ...category, subcategories: [] };
    });

    const rootCategories = [];
    validCategories.forEach((category) => {
      if (category.parent_slug && categoriesMap[category.parent_slug]) {
        categoriesMap[category.parent_slug].subcategories.push(
          categoriesMap[category.slug]
        );
      } else {
        rootCategories.push(categoriesMap[category.slug]);
      }
    });

    return rootCategories;
  };

  const getAllSubcategories = (category) => {
    const allSubcategories = [...(category.subcategories || [])];
    category.subcategories?.forEach((subcat) => {
      allSubcategories.push(...getAllSubcategories(subcat));
    });
    return allSubcategories;
  };

  const formatCategoryName = (name) => {
    if (!name) return "";
    return name
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const renderSubcategoriesPanel = () => {
    if (!activeCategory && !activeSubcategory) return null;

    const currentParent = activeSubcategory || activeCategory;
    if (!currentParent) return null;

    const displayItems = currentParent.subcategories || [];
    const showBackButton = navigationHistory.length > 1;
    const hasChildren = displayItems.length > 0;

    const chunkSize = Math.ceil(displayItems.length / 3);
    const columns = [];

    for (let i = 0; i < 3 && hasChildren; i++) {
      const start = i * chunkSize;
      const end = start + chunkSize;
      columns.push(displayItems.slice(start, end));
    }

    return (
      <div className="subcategories-panel">
        <div className="subcategories-header">
          <div className="header-top">
            {showBackButton && (
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={handleGoBack}
                className="back-button"
                size="small"
              >
                Volver
              </Button>
            )}
          </div>
          <div className="header-content">
            <div className="category-title-row">
              <h3 className="category-title">
                {formatCategoryName(currentParent.name)}
              </h3>
              <Button
                type="link"
                onClick={() => handleCategoryDirectClick(currentParent)}
                className="view-all-btn"
                size="small"
              >
                Ver todos
              </Button>
            </div>
            <span className="category-description">
              {hasChildren
                ? `Explora ${displayItems.length} ${
                    displayItems.length === 1 ? "subcategoría" : "subcategorías"
                  }`
                : "Haz clic para ver productos"}
            </span>
          </div>
        </div>

        {hasChildren ? (
          <div className="subcategories-content">
            {columns.map((column, colIndex) => (
              <div key={colIndex} className="subcategories-column">
                {column.map((item) => (
                  <div
                    key={item.slug}
                    className={`subcategory-item ${
                      item.subcategories?.length > 0 ? "has-children" : ""
                    }`}
                    onMouseEnter={() =>
                      item.subcategories?.length > 0 &&
                      handleSubcategoryHover(item)
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item.subcategories?.length > 0) {
                        handleSubcategoryHover(item);
                      } else {
                        handleSubcategoryClick(item, currentParent);
                      }
                    }}
                  >
                    <div className="subcategory-content">
                      <span className="subcategory-name">
                        {formatCategoryName(item.name)}
                      </span>
                      {item.subcategories?.length > 0 && (
                        <div className="subcategory-indicator">
                          <span className="count-badge">
                            {item.subcategories.length}
                          </span>
                          <RightOutlined className="subcategory-arrow" />
                        </div>
                      )}
                    </div>
                    <div className="subcategory-actions">
                      <Button
                        type="link"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSubcategoryClick(item, currentParent);
                        }}
                        className="view-subcategory-btn"
                      >
                        Ver
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-subcategories">
            <div className="empty-state">
              <p>No hay más subcategorías en este nivel</p>
              <br />
              <Button
                type="primary"
                onClick={() => handleCategoryDirectClick(currentParent)}
                className="view-products-btn"
              >
                Ver todos los productos
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getVisibleCategories = () => {
    return categories;
  };

  const handleGoBack = () => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop();
      const previousItem = newHistory[newHistory.length - 1];

      if (previousItem.type === "category") {
        setActiveCategory(previousItem.data);
        setActiveSubcategory(null);
      } else {
        const categoryItem = newHistory.find(
          (item) => item.type === "category"
        );
        setActiveCategory(categoryItem?.data || null);
        setActiveSubcategory(previousItem.data);
      }
      setNavigationHistory(newHistory);
    } else {
      setActiveSubcategory(null);
      setNavigationHistory([]);
    }
  };

  const openMobileCategoriesMenu = useCallback(() => {
    setMobileMenuOpen(true);
    setActiveCategory(null);
    setActiveSubcategory(null);
    setNavigationHistory([]);
    setShowAllCategories(true);
    setShowAllSubcategories(false);
  }, []);

  const closeMobileCategoriesMenu = useCallback(() => {
    setMobileMenuOpen(false);
    setActiveCategory(null);
    setActiveSubcategory(null);
    setNavigationHistory([]);
    setShowAllCategories(true);
    setShowAllSubcategories(false);
  }, []);

  const goToMobileMenuRoot = useCallback(() => {
    setActiveCategory(null);
    setActiveSubcategory(null);
    setNavigationHistory([]);
    setShowAllCategories(true);
    setShowAllSubcategories(false);
  }, []);

  const handleCategoryDirectClick = (category) => {
    if (!canBrowseMarketplace) return;
    history(`/marketplace/products/filter?category=${category.slug}`);
    setIsCategoriesOpen(false);
    setMobileMenuOpen(false);
    setActiveCategory(null);
    setActiveSubcategory(null);
    setNavigationHistory([]);
  };

  const handleSubcategoryHover = (subcategory) => {
    if (!canBrowseMarketplace) return;
    setActiveSubcategory(subcategory);
    if (subcategory.subcategories?.length > 0) {
      setNavigationHistory((prev) => [
        ...prev,
        { type: "subcategory", data: subcategory },
      ]);
    }
  };

  const handleCategoryHover = useCallback(
    (category) => {
      if (!canBrowseMarketplace) return;
      setActiveCategory(category);
      setActiveSubcategory(null);
      setNavigationHistory([{ type: "category", data: category }]);
    },
    [canBrowseMarketplace]
  );

  const handleCategoryClick = useCallback(
    (category, parentCategory = null) => {
      if (!canBrowseMarketplace) return;

      if (parentCategory) {
        history(
          `/marketplace/products/filter?category=${parentCategory.slug}&subcategory=${category.slug}`
        );
      } else {
        history(`/marketplace/products/filter?category=${category.slug}`);
      }

      setIsCategoriesOpen(false);
      setMobileMenuOpen(false);
      setActiveCategory(null);
      setActiveSubcategory(null);
      setNavigationHistory([]);
      setShowAllCategories(true);
    },
    [canBrowseMarketplace, history]
  );

  const handleSubcategoryClick = useCallback(
    (subcategory, parentItem) => {
      if (!canBrowseMarketplace) return;

      if (parentItem) {
        history(
          `/marketplace/products/filter?category=${parentItem.slug}&subcategory=${subcategory.slug}`
        );
      } else if (activeCategory) {
        history(
          `/marketplace/products/filter?category=${activeCategory.slug}&subcategory=${subcategory.slug}`
        );
      }

      setIsCategoriesOpen(false);
      setMobileMenuOpen(false);
      setActiveCategory(null);
      setActiveSubcategory(null);
      setNavigationHistory([]);
      setShowAllCategories(true);
    },
    [canBrowseMarketplace, activeCategory, history]
  );

  const handleMobileItemClick = useCallback(
    (item) => {
      if (!item) return;

      const currentParent = activeSubcategory || activeCategory;
      const hasChildren = item.subcategories?.length > 0;

      if (hasChildren) {
        if (!currentParent) {
          setActiveCategory(item);
          setActiveSubcategory(null);
          setNavigationHistory([{ type: "category", data: item }]);
        } else {
          setActiveSubcategory(item);
          setNavigationHistory((prev) => [
            ...prev,
            { type: "subcategory", data: item },
          ]);
        }
        return;
      }

      if (!currentParent) {
        handleCategoryClick(item);
        return;
      }

      handleSubcategoryClick(item, currentParent);
    },
    [
      activeCategory,
      activeSubcategory,
      handleCategoryClick,
      handleSubcategoryClick,
    ]
  );

  const handleCartClick = useCallback(() => {
    history("/marketplace/shopping-cart");
  }, [history]);

  const handleLoginClick = useCallback(() => {
    history("/login");
  }, [history]);

  const handleGoHome = useCallback(() => {
    if (roles.isProvider) {
      history(`/marketplace/suppliers/${user?.id}`);
    } else if (canBrowseMarketplace) {
      history("/marketplace");
    } else if (user?.id) {
      history(`/@p/${user.id}`);
    }
  }, [roles.isProvider, canBrowseMarketplace, user?.id, history]);

  const handleSearchButtonClick = useCallback((rawSearchValue) => {
    if (!canBrowseMarketplace) return;
    const normalizedSearch = (
      typeof rawSearchValue === "string" ? rawSearchValue : searchValue || ""
    ).trim();
    if (!normalizedSearch) return;

    setLastCommittedSearch(normalizedSearch);

    if (onSearch && typeof onSearch === "function") {
      onSearch(normalizedSearch);
    }
    window.dispatchEvent(
      new CustomEvent("marketplace_search", { detail: normalizedSearch })
    );

    if (isMobileView || isTabletView) {
      setIsMobileSearchOpen(false);
      setIsSearchExpanded(false);
    }
  }, [canBrowseMarketplace, onSearch, searchValue, isMobileView, isTabletView]);

  const handleClearSearch = useCallback(() => {
    if (!canBrowseMarketplace) return;

    isExplicitClearRef.current = true;
    setSearchValue("");
    setLastCommittedSearch("");
    history("/marketplace");
    window.dispatchEvent(new CustomEvent("marketplace_search", { detail: "" }));
  }, [canBrowseMarketplace, history]);

  const handleInputChange = useCallback(
    (e) => {
      if (!canBrowseMarketplace) return;
      const value = e?.target?.value || "";
      setSearchValue(value);

      const normalizedSearch = value.trim();
      if (!normalizedSearch) {
        if (isExplicitClearRef.current) {
          isExplicitClearRef.current = false;
          window.dispatchEvent(
            new CustomEvent("marketplace_search", { detail: "" })
          );
          return;
        }

        const fallbackSearch = (lastCommittedSearch || "").trim();
        window.dispatchEvent(
          new CustomEvent("marketplace_search", { detail: fallbackSearch })
        );
        return;
      }

      window.dispatchEvent(
        new CustomEvent("marketplace_search", { detail: normalizedSearch })
      );
    },
    [canBrowseMarketplace, lastCommittedSearch]
  );

  const handleNotificationClick = useCallback(
    (notification) => {
      if (!notification.seen) {
        markNotificationAsRead(notification.id);
      }

      let targetPath = "/@a-@n";
      if (notification.route) targetPath = notification.route;
      history(targetPath);
    },
    [markNotificationAsRead, history]
  );

  const handleDismissNotification = useCallback(
    async (event, notification) => {
      event?.stopPropagation();
      if (!notification?.id) return;
      markNotificationAsRead(notification.id);
    },
    [markNotificationAsRead]
  );

  if (!shouldShowTopBar) return null;

  const userProfileMenu = {
    items: [
      {
        key: "profile",
        icon: <UserOutlined />,
        label: "Mi Perfil",
        onClick: () => {
          if (user?.id) {
            history(
              roles.isProvider
                ? `/marketplace/suppliers/${user.id}`
                : `/@p/${user.id}`
            );
          }
        },
      },
      { type: "divider" },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: "Cerrar Sesión",
        onClick: onLogout,
        danger: true,
      },
    ],
  };

  const unreadNotifications = localNotifications.filter(
    (notification) => notification.seen === false || notification.seen === 0
  );

  const notificationDropdownContent = (
    <div
      className="notifications-panel"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="notifications-panel-header">
        <div>
          <div className="notifications-panel-title">Notificaciones</div>
          <div className="notifications-panel-subtitle">
            {localUnreadCount > 0
              ? `Tienes ${localUnreadCount} notificaciones nuevas`
              : "No tienes notificaciones nuevas"}
          </div>
        </div>
        {localUnreadCount > 0 && (
          <Button type="link" size="small" onClick={markAllAsRead}>
            Marcar todas como leídas
          </Button>
        )}
      </div>

      <div className="notifications-panel-list">
        {unreadNotifications.length === 0 ? (
          <div className="notification-empty-state">
            No tienes notificaciones pendientes
          </div>
        ) : (
          unreadNotifications.map((notification, index) => (
            <div
              key={`notification-${notification.id ?? index}`}
              className={`notification-card ${
                notification.seen ? "read" : "unread"
              }`}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="notification-card-icon">
                <BellOutlined />
              </div>

              <div className="notification-card-content">
                <div className="notification-card-title">
                  {notification.title || "Sin título"}
                </div>
                <div className="notification-card-description">
                  {notification.description || "Sin descripción"}
                </div>
                <div className="notification-card-time">
                  {moment(
                    notification.createdAt || notification.created_at
                  ).fromNow()}
                </div>
              </div>

              <div className="notification-card-actions">
                <span className="notification-new-dot" />
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={(event) =>
                    handleDismissNotification(event, notification)
                  }
                />
              </div>
            </div>
          ))
        )}
      </div>

      <div className="notifications-panel-footer">
        <Button type="link" block onClick={() => history("/@a-@n")}>
          Ver todas las notificaciones
        </Button>
      </div>
    </div>
  );

  return (
    <div
      className={`top-banner-wrapper marketplace-header ${
        pathChecks.isCartPage ? "cart-page-header" : ""
      }`}
    >
      <div className="top-bar-main">
        <div className="top-bar-left">
          <div className="logo-section">
            {menuButton}
            <img
              src={Logo}
              alt="Logo"
              className="logo-img"
              onClick={handleGoHome}
              style={{ cursor: "pointer" }}
            />
            {title && <span className="page-title">{title}</span>}
          </div>
        </div>

        <div className="top-bar-center">
          {showMarketplaceFeatures &&
            canBrowseMarketplace &&
            (!isMobileView && !isTabletView ? (
              <div className="marketplace-search-section">
                <div className="search-categories-container">
                  <Dropdown
                    placement="bottomLeft"
                    trigger={["click"]}
                    overlayClassName="categories-dropdown-marketplace"
                    disabled={loadingCategories}
                    open={isCategoriesOpen}
                    onOpenChange={setIsCategoriesOpen}
                    dropdownRender={() => (
                      <div className="categories-dropdown-container">
                        <div className="main-categories-menu">
                          <div className="categories-list">
                            {getVisibleCategories().map((category) => (
                              <div
                                key={category.slug}
                                className={`category-main-item ${
                                  activeCategory?.slug === category.slug
                                    ? "active"
                                    : ""
                                }`}
                                onMouseEnter={() =>
                                  handleCategoryHover(category)
                                }
                                onClick={() => {
                                  if (category.subcategories?.length > 0) {
                                    handleCategoryHover(category);
                                  } else {
                                    handleCategoryClick(category);
                                  }
                                }}
                              >
                                <div className="category-main-content">
                                  <span className="category-main-name">
                                    {formatCategoryName(category.name)}
                                  </span>
                                  {category.subcategories?.length > 0 && (
                                    <div className="category-indicator">
                                      <span className="count-badge">
                                        {getAllSubcategories(category).length}
                                      </span>
                                      <RightOutlined className="category-main-arrow" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {renderSubcategoriesPanel()}
                      </div>
                    )}
                  >
                    <Button
                      className="categories-button-marketplace"
                      icon={<AppstoreOutlined />}
                      loading={loadingCategories}
                    >
                      <span className="button-text">
                        {loadingCategories ? "Cargando..." : "Categorías"}
                      </span>
                    </Button>
                  </Dropdown>

                  <div className="custom-search-container">
                    <Input
                      ref={searchInputRef}
                      placeholder="Buscar productos, categorías, marcas..."
                      className="custom-search-input"
                      value={searchValue}
                      onChange={handleInputChange}
                      onPressEnter={(e) =>
                        handleSearchButtonClick(e?.target?.value)
                      }
                      onClear={handleClearSearch}
                      allowClear
                    />
                    <Button
                      type="primary"
                      className="custom-search-button"
                      onClick={handleSearchButtonClick}
                      icon={<SearchOutlined />}
                    >
                      <span className="search-text">Buscar</span>
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="marketplace-search-section"
                ref={searchContainerRef}
              >
                <div className="search-compact-view">
                  <Button
                    className="categories-button-marketplace"
                    icon={<AppstoreOutlined />}
                    onClick={openMobileCategoriesMenu}
                  />
                  <Button
                    className="search-icon-button"
                    icon={<SearchOutlined />}
                    onClick={() => setIsMobileSearchOpen(true)}
                  />
                </div>
              </div>
            ))}
        </div>

        <div className="top-bar-right">
          {canSeeDollarRate && !isMobileView && !isTabletView && (
            <div className="dollar-rate-wrapper">
              <DollarRateCard compact={true} />
            </div>
          )}

          {user?.id ? (
            <>
              <Dropdown
                placement="bottomRight"
                trigger={["click"]}
                overlayClassName="notifications-dropdown"
                dropdownRender={() => notificationDropdownContent}
              >
                <Badge
                  count={localUnreadCount}
                  overflowCount={99}
                  className="notification-badge"
                >
                  <Button
                    shape="circle"
                    className="icon-button circular-icon"
                    icon={<BellOutlined />}
                    loading={false}
                  />
                </Badge>
              </Dropdown>

              {canBrowseMarketplace &&
                !pathChecks.isCartPage &&
                !pathChecks.isCheckoutPage && (
                  <Badge
                    count={localCartCount}
                    showZero={false}
                    className="cart-badge"
                  >
                    <Button
                      shape="circle"
                      className="icon-button circular-icon"
                      icon={<ShoppingCartOutlined />}
                      onClick={handleCartClick}
                    />
                  </Badge>
                )}

              <Dropdown
                menu={userProfileMenu}
                placement="bottomRight"
                trigger={["click"]}
              >
                <div className="user-avatar" style={{ cursor: "pointer" }}>
                  <Avatar
                    size="default"
                    icon={<UserOutlined />}
                    src={user?.logo}
                    className="avatar"
                  />
                  {!isMobileView && !isTabletView && (
                    <span className="user-name">
                      {user?.name?.split(" ")[0] || "Usuario"}
                    </span>
                  )}
                </div>
              </Dropdown>
            </>
          ) : (
            <Button
              type="primary"
              onClick={handleLoginClick}
              className="login-button"
            >
              {isMobileView || isTabletView
                ? "Iniciar sesión"
                : "Iniciar sesión o registrarse"}
            </Button>
          )}
        </div>
      </div>

      {canBrowseMarketplace && (
        <>
          <Drawer
            title="Buscar productos"
            placement="top"
            onClose={() => setIsMobileSearchOpen(false)}
            open={isMobileSearchOpen}
            height="auto"
            className="mobile-search-drawer"
            closable={true}
            styles={{
              header: {
                background: "#0F5197",
                borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <div className="mobile-search-content">
              <div className="mobile-search-input-container">
                <Input
                  ref={searchInputRef}
                  placeholder="Buscar productos, categorías, marcas..."
                  className="mobile-search-input"
                  value={searchValue}
                  onChange={handleInputChange}
                  onPressEnter={(e) =>
                    handleSearchButtonClick(e?.target?.value)
                  }
                  onClear={handleClearSearch}
                  allowClear
                  autoFocus
                  prefix={<SearchOutlined style={{ color: "#0F5197" }} />}
                  suffix={
                    searchValue && (
                      <Button
                        type="primary"
                        size="small"
                        onClick={handleSearchButtonClick}
                        style={{
                          background: "#0F5197",
                          borderColor: "#0F5197",
                        }}
                      >
                        Buscar
                      </Button>
                    )
                  }
                />
              </div>
            </div>
          </Drawer>

          <Drawer
            placement="left"
            width="85%"
            onClose={closeMobileCategoriesMenu}
            open={mobileMenuOpen}
            closable={false}
            className="mobile-categories-drawer"
          >
            <div className="mobile-categories-menu">
              <div className="mobile-menu-header">
                <div className="mobile-menu-nav">
                  {activeCategory || activeSubcategory ? (
                    <Button
                      type="text"
                      icon={<LeftOutlined />}
                      onClick={goToMobileMenuRoot}
                      className="mobile-back-button"
                      aria-label="Volver a categorías principales"
                    />
                  ) : (
                    <div className="mobile-menu-title">
                      <h3>Categorías</h3>
                    </div>
                  )}

                  {(activeCategory || activeSubcategory) && (
                    <div className="mobile-current-category">
                      {formatCategoryName(
                        (activeSubcategory || activeCategory)?.name
                      )}
                    </div>
                  )}
                </div>

                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={closeMobileCategoriesMenu}
                  className="mobile-close-button"
                />
              </div>

              <div className="mobile-menu-content">
                {(() => {
                  const currentParent = activeSubcategory || activeCategory;
                  const mobileItems = currentParent
                    ? currentParent.subcategories || []
                    : getVisibleCategories();

                  if (!mobileItems.length) {
                    return (
                      <div className="mobile-no-subcategories">
                        <div className="mobile-empty-state">
                          <p>No hay más subcategorías en este nivel</p>
                          {currentParent && (
                            <Button
                              type="primary"
                              onClick={() =>
                                currentParent &&
                                handleCategoryDirectClick(currentParent)
                              }
                              className="mobile-view-products-btn"
                            >
                              Ver todos los productos
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <>
                      {currentParent && (
                        <div className="mobile-menu-return">
                          <Button
                            type="default"
                            icon={<LeftOutlined />}
                            onClick={goToMobileMenuRoot}
                            className="mobile-menu-return-button"
                          >
                            Volver al menú
                          </Button>
                        </div>
                      )}

                      <div
                        className={
                          currentParent
                            ? "mobile-subcategories-list"
                            : "mobile-categories-list"
                        }
                      >
                        {mobileItems.map((item) => {
                          const hasChildren = item.subcategories?.length > 0;

                          return (
                            <div
                              key={item.slug}
                              className={`${
                                currentParent
                                  ? "mobile-subcategory-item"
                                  : "mobile-category-item"
                              } ${hasChildren ? "has-children" : ""}`}
                              onClick={() => handleMobileItemClick(item)}
                            >
                              <div
                                className={
                                  currentParent
                                    ? "mobile-subcategory-content"
                                    : "mobile-category-content"
                                }
                              >
                                <span
                                  className={
                                    currentParent
                                      ? "mobile-subcategory-name"
                                      : "mobile-category-name"
                                  }
                                >
                                  {formatCategoryName(item.name)}
                                </span>

                                {hasChildren && (
                                  <div
                                    className={
                                      currentParent
                                        ? "mobile-subcategory-indicator"
                                        : "mobile-category-indicator"
                                    }
                                  >
                                    <span className="mobile-count-badge">
                                      {item.subcategories.length}
                                    </span>
                                    <RightOutlined
                                      className={
                                        currentParent
                                          ? "mobile-subcategory-arrow"
                                          : "mobile-category-arrow"
                                      }
                                    />
                                  </div>
                                )}
                              </div>

                              <div
                                className={
                                  currentParent
                                    ? "mobile-subcategory-actions"
                                    : "mobile-category-actions"
                                }
                              >
                                <Button
                                  type="text"
                                  className="mobile-view-btn"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    if (!currentParent) {
                                      handleCategoryClick(item);
                                    } else {
                                      handleSubcategoryClick(
                                        item,
                                        currentParent
                                      );
                                    }
                                  }}
                                >
                                  Ver
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </Drawer>
        </>
      )}
    </div>
  );
};

export default React.memo(TopBar, (prevProps, nextProps) => {
  return (
    prevProps.title === nextProps.title &&
    prevProps.isCategory === nextProps.isCategory
  );
});
